/** WhatsApp accepts JPEG and PNG pictures up to 5 MB. Anything else pasted or dropped is converted or shrunk here. */
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const NATIVE_TYPES = ['image/jpeg', 'image/png'];
const MAX_SIDE = 2048;

/** The first picture in a paste or drop, if there is one. */
export function firstImage(files: FileList | File[] | null | undefined): File | undefined {
  return Array.from(files ?? []).find((f) => f.type.startsWith('image/'));
}

export function formatBytes(n: number): string {
  return n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

const renamed = (name: string, ext: string) => `${name.replace(/\.[^./\\]+$/, '') || 'image'}.${ext}`;

/**
 * A file WhatsApp will take: JPEG/PNG as they are when small enough, otherwise re-encoded in the browser
 * (WebP/GIF/BMP → PNG, or JPEG when that would still be too big; big pictures are scaled down).
 * Throws an Error with a message fit for a toast.
 */
export async function prepareImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) throw new Error('Only pictures can be sent from here');
  if (NATIVE_TYPES.includes(file.type) && file.size <= IMAGE_MAX_BYTES) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('This browser cannot read that picture. Use a JPEG or PNG.');
  }
  try {
    const draw = (scale: number) => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('This browser cannot convert pictures');
      return { canvas, ctx };
    };
    const fit = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));

    // A picture that is only the wrong format keeps its pixels: PNG.
    if (!NATIVE_TYPES.includes(file.type)) {
      const { canvas, ctx } = draw(1);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const png = await toBlob(canvas, 'image/png');
      if (png && png.size <= IMAGE_MAX_BYTES) return new File([png], renamed(file.name, 'png'), { type: 'image/png' });
    }
    // Too big (or PNG too heavy): JPEG, scaled down until it fits. JPEG has no transparency, so paint white first.
    for (const factor of [1, 0.75, 0.5, 0.35]) {
      const { canvas, ctx } = draw(fit * factor);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const jpeg = await toBlob(canvas, 'image/jpeg', 0.85);
      if (jpeg && jpeg.size <= IMAGE_MAX_BYTES) return new File([jpeg], renamed(file.name, 'jpg'), { type: 'image/jpeg' });
    }
    throw new Error('That picture is too large to send');
  } finally {
    bitmap.close();
  }
}
